import { useEffect, useState } from 'react';
import { getWindow } from '../../utils/environment';

const COLORS_DARK = [
  '#ff9ec8', '#ffb6d9', '#e891c0', '#f472b6',
  '#fbb6ce', '#ec4899', '#db2777', '#c084fc',
  '#e879f9', '#a855f7',
];

const COLORS_LIGHT = [
  '#db2777', '#be185d', '#9d174d', '#831843',
  '#7c3aed', '#6d28d9', '#a21caf', '#c026d3',
  '#ec4899', '#9333ea',
];

const SYMBOLS = ['</', '{}', '=>', '[]', '()', '✦', '◆', '○', '△', '+'];

const TRAIL_LENGTH = 16;

const getTrailTheme = (isDarkMode) => {
  if (isDarkMode) {
    return {
      colors: COLORS_DARK,
      trailOpacity: 0.58,
      cursorOpacity: 0.82,
      particleAlpha: 0.62,
      ringRgb: ['255,182,213', '232,145,192', '168,85,247'],
      coreRgb: '255, 182, 217',
      crossRgb: '255, 182, 213',
    };
  }
  return {
    colors: COLORS_LIGHT,
    trailOpacity: 0.76,
    cursorOpacity: 0.92,
    particleAlpha: 0.75,
    ringRgb: ['190,24,93', '157,23,77', '109,40,217'],
    coreRgb: '190, 24, 93',
    crossRgb: '157, 23, 77',
  };
};

const TRAIL_MEDIA_QUERIES = [
  '(prefers-reduced-motion: reduce)',
  '(pointer: coarse)',
  '(hover: none)',
];

const isTrailSupported = () => {
  const win = getWindow();
  if (!win) return false;
  return !TRAIL_MEDIA_QUERIES.some((query) => win.matchMedia(query).matches);
};

class Particle {
  constructor(x, y, vx, vy, type, colors, particleAlpha) {
    this.x = x;
    this.y = y;
    this.vx = vx + (Math.random() - 0.5) * 1.5;
    this.vy = vy + (Math.random() - 0.5) * 1.5;
    this.alpha = 1;
    this.particleAlpha = particleAlpha;
    this.color = colors[Math.floor(Math.random() * colors.length)];
    this.type = type;
    this.life = 0;
    this.maxLife = 32 + Math.random() * 32;
    this.size = Math.random() * 2.8 + 1.1;
    this.rotation = Math.random() * Math.PI * 2;
    this.rotSpeed = (Math.random() - 0.5) * 0.12;
    if (type === 'symbol') {
      this.symbol = SYMBOLS[Math.floor(Math.random() * SYMBOLS.length)];
      this.fontSize = 10 + Math.random() * 7;
    }
  }

  update() {
    this.x += this.vx;
    this.y += this.vy;
    this.vy += 0.04;
    this.vx *= 0.98;
    this.life++;
    this.alpha = 1 - this.life / this.maxLife;
    this.rotation += this.rotSpeed;
  }

  draw(context) {
    context.save();
    context.globalAlpha = Math.max(0, this.alpha * this.particleAlpha);
    context.translate(this.x, this.y);
    context.rotate(this.rotation);
    if (this.type === 'dot') {
      context.beginPath();
      context.arc(0, 0, this.size, 0, Math.PI * 2);
      context.fillStyle = this.color;
      context.fill();
    } else if (this.type === 'ring') {
      context.beginPath();
      context.arc(0, 0, this.size * 2.5, 0, Math.PI * 2);
      context.strokeStyle = this.color;
      context.lineWidth = 0.8;
      context.stroke();
    } else if (this.type === 'symbol') {
      context.font = `${this.fontSize}px monospace`;
      context.fillStyle = this.color;
      context.textAlign = 'center';
      context.textBaseline = 'middle';
      context.fillText(this.symbol, 0, 0);
    } else if (this.type === 'cross') {
      context.strokeStyle = this.color;
      context.lineWidth = 1;
      context.beginPath();
      context.moveTo(-this.size * 2, 0);
      context.lineTo(this.size * 2, 0);
      context.moveTo(0, -this.size * 2);
      context.lineTo(0, this.size * 2);
      context.stroke();
    }
    context.restore();
  }
}

export const useCursorTrailEnabled = () => {
  const [enabled, setEnabled] = useState(() => isTrailSupported());

  useEffect(() => {
    const win = getWindow();
    if (!win) return undefined;

    const mediaLists = TRAIL_MEDIA_QUERIES.map((query) => win.matchMedia(query));
    const update = () => setEnabled(isTrailSupported());

    for (const mql of mediaLists) {
      mql.addEventListener('change', update);
    }
    update();

    return () => {
      for (const mql of mediaLists) {
        mql.removeEventListener('change', update);
      }
    };
  }, []);

  return enabled;
};

export const useCursorTrailEngine = (canvasRef, isEnabled, isDarkMode) => {
  useEffect(() => {
    if (!isEnabled) return undefined;

    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;

    const theme = getTrailTheme(isDarkMode);

    let mouse = { x: -200, y: -200 };
    let prevMouse = { x: -200, y: -200 };
    let particles = [];
    let trail = [];
    let frame = 0;
    let frameId = 0;

    const resizeCanvas = () => {
      const win = getWindow();
      if (!win) return;
      canvas.width = win.innerWidth;
      canvas.height = win.innerHeight;
    };

    const drawCursor = (x, y) => {
      const t = Date.now() * 0.003;
      ctx.save();
      ctx.translate(x, y);

      for (let i = 0; i < 3; i++) {
        const angle = t * (1 + i * 0.4) + (i * Math.PI * 2) / 3;
        const r = 12 + i * 4;
        const ox = Math.cos(angle) * 2;
        const oy = Math.sin(angle) * 2;
        ctx.beginPath();
        ctx.arc(ox, oy, r, 0, Math.PI * 2);
        const ringRgb = theme.ringRgb[i];
        ctx.strokeStyle = `rgba(${ringRgb}, ${(0.55 - i * 0.12) * theme.cursorOpacity})`;
        ctx.lineWidth = 0.9;
        ctx.stroke();
      }

      ctx.beginPath();
      ctx.arc(0, 0, 4.5, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(${theme.coreRgb}, ${0.85 * theme.cursorOpacity})`;
      ctx.fill();

      const crossSize = 8;
      const crossGap = 4;
      ctx.strokeStyle = `rgba(${theme.crossRgb}, ${0.8 * theme.cursorOpacity})`;
      ctx.lineWidth = 1.1;
      ctx.beginPath();
      ctx.moveTo(-crossSize, 0);
      ctx.lineTo(-crossGap, 0);
      ctx.moveTo(crossGap, 0);
      ctx.lineTo(crossSize, 0);
      ctx.moveTo(0, -crossSize);
      ctx.lineTo(0, -crossGap);
      ctx.moveTo(0, crossGap);
      ctx.lineTo(0, crossSize);
      ctx.stroke();

      ctx.restore();
    };

    const onMouseMove = (e) => {
      prevMouse = { ...mouse };
      mouse = { x: e.clientX, y: e.clientY };

      const speed = Math.hypot(mouse.x - prevMouse.x, mouse.y - prevMouse.y);
      const dx = mouse.x - prevMouse.x;
      const dy = mouse.y - prevMouse.y;
      const norm = Math.max(speed, 0.1);

      trail.unshift({ x: mouse.x, y: mouse.y });
      if (trail.length > TRAIL_LENGTH) trail.pop();

      const burst = Math.min(Math.floor(speed * 0.28), 4);
      for (let i = 0; i < burst; i++) {
        const r = Math.random();
        let type;
        if (r < 0.55) type = 'dot';
        else if (r < 0.78) type = 'ring';
        else if (r < 0.9) type = 'symbol';
        else type = 'cross';

        const perp = { x: -dy / norm, y: dx / norm };
        const spread = (Math.random() - 0.5) * 2.5;
        particles.push(
          new Particle(
            mouse.x + perp.x * spread * 4,
            mouse.y + perp.y * spread * 4,
            (-dx / norm) * (Math.random() * 2) + spread * 0.3,
            (-dy / norm) * (Math.random() * 2) - Math.random() * 1.5,
            type,
            theme.colors,
            theme.particleAlpha,
          ),
        );
      }

      if (speed > 24 && frame % 4 === 0) {
        for (let i = 0; i < 2; i++) {
          const angle = Math.random() * Math.PI * 2;
          const spd = Math.random() * 3 + 1;
          particles.push(
            new Particle(
              mouse.x,
              mouse.y,
              Math.cos(angle) * spd,
              Math.sin(angle) * spd,
              'symbol',
              theme.colors,
              theme.particleAlpha,
            ),
          );
        }
      }
    };

    const animate = () => {
      frame++;
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      for (let i = 0; i < trail.length; i++) {
        const t = trail[i];
        ctx.beginPath();
        ctx.arc(t.x, t.y, Math.max(0.5, 4.8 - i * 0.24), 0, Math.PI * 2);
        ctx.fillStyle = theme.colors[i % theme.colors.length];
        ctx.globalAlpha = Math.max(0, (1 - i * 0.07) * theme.trailOpacity);
        ctx.fill();
        ctx.globalAlpha = 1;
      }

      particles = particles.filter((p) => p.life < p.maxLife);
      for (const p of particles) {
        p.update();
        p.draw(ctx);
      }

      ctx.globalAlpha = 1;
      if (mouse.x > 0) drawCursor(mouse.x, mouse.y);

      frameId = requestAnimationFrame(animate);
    };

    resizeCanvas();
    const win = getWindow();
    win?.addEventListener('resize', resizeCanvas);
    win?.addEventListener('mousemove', onMouseMove);
    frameId = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(frameId);
      win?.removeEventListener('resize', resizeCanvas);
      win?.removeEventListener('mousemove', onMouseMove);
      particles = [];
      trail = [];
    };
  }, [canvasRef, isEnabled, isDarkMode]);
};
